/**
 * Spaces resource: TanStack-backed read + mutation hooks. Canonical client
 * interface for everything spaces-related.
 *
 * Reads (`useSpacesPage` / `useSpaceCount` / `useSpaceSearch` / `useSpace`, and
 * the per-field `useSpaceName` / `useSpaceFeatures`) hide the TanStack
 * machinery — consumers don't import `@tanstack/react-query` themselves.
 * Mutations (`useCreateSpace` / `useUpdateSpace` / `useSetSpaceFeatures`)
 * auto-invalidate once the write finishes, so the affected TanStack queries
 * refetch.
 *
 * `useSpaceFeaturesQuery` and the `spaceFeaturesQueryOptions` builder behind
 * the suspense read are the config layer's entry points to the features
 * field. They take their identity as an argument, because both callers run
 * where the space hooks' context reads aren't available.
 *
 * `useCommentTeaser` and the featured-comment writes (`featureCommentAsync` /
 * `unfeatureCommentAsync`) also live here: they back the teaser embed and the
 * space's at-most-one featured comment, which are space-level state even though
 * the payload previews a comment. They invalidate the comment tree
 * (`./comment.ts`) on success. `useLinkedStories` reads the space's links to
 * story instances.
 *
 * See `./README.md` for the convention this file establishes for future
 * resource ports.
 */

import type { RouterUtils } from '@orpc/react-query';
import type { IsoLocale } from '@repo/shared/locale';
import type { ModerationMode } from '@repo/shared/moderation-modes';
import { displayTextOf, type MultiLanguageString } from '@repo/shared/orpc/multi-language';
import type { Comment } from '@repo/shared/orpc/procedures/comments';
import type { CommentTeaserPreview, LinkedStory, Space } from '@repo/shared/orpc/procedures/spaces';
import type { ReactionSet } from '@repo/shared/reaction-sets';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSiloLanguage } from 'component/translation';
import { useApiQueryUtils } from 'system/api-client-context';
import type { ApiClient } from 'system/orpc';
import { type Datastore, useDatastore, useInstanceKey, useSiloKey } from 'util/datastore';
import type { FeaturesQueryOptions } from 'util/features';
import { fireAndForget, useDebouncedValue } from 'util/hooks';

import { invalidateCommentTree } from './comment';
import { nullOnNotFound } from './null-on-not-found';
import { instanceParamsOf, siloParamsOf } from './silo-params';
import { useOffsetPage } from './use-offset-paginated-query';
import { useInstanceMutation, useSiloMutation } from './use-silo-mutation';
import { useSiloQuery } from './use-silo-query';

export type { LinkedStory, Space } from '@repo/shared/orpc/procedures/spaces';

/**
 * How many matches a space picker shows. A space past them is found by
 * searching for it.
 */
const SPACE_SEARCH_LIMIT = 100;

export type SpaceListFilter = NonNullable<Parameters<ApiClient['spaces']['list']>[0]['query']>;

/**
 * One numbered page of the spaces matching `filter`, fetched by offset so a
 * table can jump straight to any page. `total` counts every match, not
 * just the page. While another page loads, `spaces` is `undefined` and
 * `isLoading` is true, but `total` keeps the last answer so a pagination
 * row doesn't disappear and reappear.
 * `includePreview` asks for each row's engagement preview, which costs a read
 * per space server-side — set it only where the cards actually render one.
 */
export function useSpacesPage({
    filter,
    pageIndex,
    pageSize,
    includePreview,
}: {
    filter: SpaceListFilter;
    pageIndex: number;
    pageSize: number;
    includePreview?: boolean;
}) {
    const utils = useApiQueryUtils();
    const query = useOffsetPage({
        procedure: utils.spaces.list,
        input: { offset: pageIndex * pageSize, limit: pageSize, query: filter, includePreview },
    });
    return {
        spaces: query.isPlaceholderData ? undefined : query.data?.items,
        total: query.data?.total,
        isLoading: query.isLoading || query.isPlaceholderData,
        isError: query.isError,
    };
}

/**
 * How many spaces match `filter`, or `undefined` until the count has loaded.
 */
export function useSpaceCount(filter: SpaceListFilter = {}): number | undefined {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.spaces.list, input: { limit: 1, query: filter } });
    return query.data?.total;
}

/**
 * The spaces a picker offers for `search`, newest first: the first
 * `SPACE_SEARCH_LIMIT` whose name contains the text, matched on the server.
 * The text is debounced, so typing sends one request when the user pauses.
 */
export function useSpaceSearch({ search, status }: { search: string; status?: SpaceListFilter['status'] }) {
    const name = useDebouncedValue(search.trim());
    const { spaces, isLoading } = useSpacesPage({
        filter: { name: name || undefined, status },
        pageIndex: 0,
        pageSize: SPACE_SEARCH_LIMIT,
    });
    return { spaces: spaces ?? [], isLoading };
}

/**
 * Canonical `spaces.get` query options: every observer of a space's cache
 * entry builds on these, so the entry has one shape everywhere. The entry is
 * the space, or `null` when the caller cannot see it — the server answers
 * NOT_FOUND for a missing space and for a draft hidden from a non-admin, and
 * both are ordinary answers about the world, not failures, so the fetch
 * resolves rather than rejects. Any other rejection stays an error.
 */
function spaceQueryOptions(utils: RouterUtils<ApiClient>, { siloKey, spaceKey, locale }: SpaceIdentity) {
    const options = utils.spaces.get.queryOptions({ input: { siloKey, spaceKey, locale } });
    return {
        ...options,
        queryFn: nullOnNotFound(options.queryFn),
    };
}

/**
 * One space; `null` when it does not exist or is hidden from the caller, and
 * `undefined` while loading, when disabled (`spaceKey` is `null`), or when
 * the fetch fails.
 */
export function useSpace(spaceKey: string | null): Space | null | undefined {
    const siloKey = useSiloKey();
    const locale = useSiloLanguage();
    const utils = useApiQueryUtils();
    const query = useQuery({
        ...spaceQueryOptions(utils, { siloKey, spaceKey: spaceKey ?? '', locale }),
        enabled: spaceKey !== null,
    });
    // A null key has no space whatever the cache holds, so don't hand back a
    // disabled query's `data`.
    return spaceKey === null ? undefined : query.data;
}

/**
 * The current space's name as a `MultiLanguageString`, or `null` while
 * loading. First-class field of the space resource — the reactive replacement
 * for `useInstanceProperty(nameProperty)` in space context. Returns the raw
 * `MultiLanguageString` so callers decide whether they want the original
 * (`originalTextOf`, e.g. an edit-form default, a prompt, or an analytics
 * event) or the display form (`useTranslatedSpaceName` / `displayTextOf`, e.g.
 * rendering a heading).
 */
export function useSpaceName(): MultiLanguageString | null {
    const space = useSpace(useInstanceKey());
    return space ? space.name : null;
}

/**
 * The current space's name in the viewer's own language, falling back to the
 * authored name when it has no translation for that locale, or `null` while
 * loading. The reader-facing counterpart to `useSpaceName` — the display side
 * of the same server-resolved `MultiLanguageString`.
 */
export function useTranslatedSpaceName(): string | null {
    const name = useSpaceName();
    const isoLocale = useSiloLanguage();
    return name !== null ? displayTextOf(name, isoLocale) : null;
}

/**
 * The current space's per-space feature toggles, or `null` while loading and
 * when the space has none set. First-class field of the space resource. Pair
 * with `useSetSpaceFeature` to write one key back.
 */
export function useSpaceFeatures(): Record<string, boolean> | null {
    const space = useSpace(useInstanceKey());
    return space?.features ?? null;
}

/**
 * The current space's moderation mode, or `null` while loading. Every space
 * runs in a mode once loaded. The only way to read it: the mode keys behind it
 * are server-side storage and never reach `useSpaceFeatures`. Write it back
 * through `spaces.update`'s own `moderationMode` field.
 *
 * Resolves without a wait in practice — the instance mount already suspends on
 * this query (`spaceFeaturesQueryOptions` shares its key), so by the time a
 * component inside the mount renders, the space is in cache.
 */
export function useSpaceModerationMode(): ModerationMode | null {
    const space = useSpace(useInstanceKey());
    return space?.moderationMode ?? null;
}

/**
 * The current space's reaction set, or `null` while loading. First-class field
 * of the space resource: read it rather than picking the set out of
 * `useSpaceFeatures`, and write it back through `spaces.update`'s own
 * `reactionSet` field.
 */
export function useSpaceReactionSet(): ReactionSet | null {
    const space = useSpace(useInstanceKey());
    return space?.reactionSet ?? null;
}

/**
 * Whether the current space is closed to new contributions, which every write
 * affordance reads before it renders — reply, edit, delete, react, vote, and
 * the composer.
 *
 * The space resource's `status` is the one answer, so an instance that is not
 * a space reads `false`: the entry is `null` and nothing is closed. A space's
 * screen already blocks on `spaces.get` settling (`useSpaceNotFound`), so by
 * the time a write affordance mounts, this is the resolved status rather than
 * the loading default.
 */
export function useIsReadOnly(): boolean {
    const space = useSpace(useInstanceKey());
    return space?.status === 'closed';
}

/**
 * The same read as `useSpaceFeatures`, for the config layer: it takes its
 * identity as an argument rather than from context, and it reports whether
 * there is anything left to wait for.
 *
 * `enabled: false` is how a caller says "this instance is not a space": the
 * query never runs, `features` stays `null`, and `isResolved` is immediately
 * true. A space the caller cannot see also resolves — the entry is `null`, so
 * `features` stays `null` and the caller's screen shows its own not-found
 * state. A *failed* read is deliberately not resolved, so a gate holds rather
 * than assembling a screen against the defaults alone.
 */
export function useSpaceFeaturesQuery(
    { siloKey, spaceKey, locale }: SpaceIdentity,
    { enabled = true }: { enabled?: boolean } = {},
): { features: Record<string, boolean> | null; isResolved: boolean } {
    const utils = useApiQueryUtils();
    const queryEnabled = enabled && !!spaceKey;
    const query = useQuery({ ...spaceQueryOptions(utils, { siloKey, spaceKey, locale }), enabled: queryEnabled });
    return { features: query.data?.features ?? null, isResolved: !queryEnabled || query.isSuccess };
}

/** Which space a feature map belongs to. */
type SpaceIdentity = { siloKey: string; spaceKey: string; locale: IsoLocale };

/**
 * The space `FeaturesSource`: a space's toggles are a field of the space
 * resource, and its `instanceKey` is the space's `spaceKey`. Normalized via
 * `select` to the feature map — empty for a space the caller cannot see,
 * since a nonexistent instance has nothing toggled; whether the space exists
 * is `useSpaceNotFound`'s question. Same query key as `useSpace`, so every
 * observer shares one cache entry and any update to it — an optimistic write,
 * an invalidation — reaches them all. `select` runs per observer, so this
 * doesn't change what any other consumer of the key reads.
 */
export function spaceFeaturesQueryOptions(
    utils: RouterUtils<ApiClient>,
    { siloKey, instanceKey, locale }: { siloKey: string; instanceKey: string; locale: IsoLocale },
): FeaturesQueryOptions {
    return {
        ...spaceQueryOptions(utils, { siloKey, spaceKey: instanceKey, locale }),
        select: (space: Space | null): Record<string, boolean> => space?.features ?? {},
    };
}

/**
 * Whether `spaces.get` for `spaceKey` has resolved yet, and whether it
 * resolved to `null` — the space is missing, or a draft hidden from a
 * non-admin. `isLoading` lets a screen block rendering until the check has
 * settled at least once, so hidden draft content is never shown even
 * momentarily; `notFound` is only meaningful once `isLoading` is false.
 */
export function useSpaceNotFound(spaceKey: string): { isLoading: boolean; notFound: boolean } {
    const siloKey = useSiloKey();
    const utils = useApiQueryUtils();
    const locale = useSiloLanguage();
    const query = useQuery(spaceQueryOptions(utils, { siloKey, spaceKey, locale }));
    return { isLoading: query.isLoading, notFound: query.data === null };
}

// Non-reactive fetch of a space through the `spaces.get` query cache
// (`ensureQueryData` — cached value when present, fetches only when it isn't).
// Backs the `getSpaceNameAsync` callback helper.
async function getSpaceAsync(datastore: Datastore, spaceKey: string): Promise<Space | null> {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    return queryClient.ensureQueryData(spaceQueryOptions(utils, { ...siloParamsOf(datastore), spaceKey }));
}

/**
 * The current space's name, for callbacks where a hook (`useSpaceName`) can't
 * run. Rejects when the caller cannot see its own space — a callback is
 * always running inside the instance, so that is a failure, not an answer.
 */
export async function getSpaceNameAsync(datastore: Datastore): Promise<MultiLanguageString> {
    const space = await getSpaceAsync(datastore, datastore.getInstanceKey());
    if (space === null) {
        throw new Error('The current space is missing or hidden.');
    }

    return space.name;
}

/**
 * Create a space. Auto-invalidates the silo's spaces list on success.
 * Exposes the mutation's `mutateAsync` as `createSpaceAsync` so call sites
 * don't have to rename it themselves.
 */
export function useCreateSpace() {
    const siloKey = useSiloKey();
    const queryClient = useQueryClient();
    const utils = useApiQueryUtils();
    const mutation = useSiloMutation({
        procedure: utils.spaces.create,
        onSuccess: () => {
            fireAndForget(
                queryClient.invalidateQueries({
                    queryKey: utils.spaces.list.key({ input: { siloKey } }),
                }),
            );
        },
    });
    return { ...mutation, createSpaceAsync: mutation.mutateAsync };
}

type UpdateSpaceInput = Parameters<ApiClient['spaces']['update']>[0];

/**
 * The `spaces.get` cache key for one space, off a `Datastore`. Takes only the
 * two identity fields, so passing a whole `spaces.update` input can't key a
 * different query than the read it means to patch.
 */
function spaceGetQueryKey(datastore: Datastore, { siloKey, spaceKey }: { siloKey: string; spaceKey: string }) {
    return datastore.getApiQueryUtils().spaces.get.queryOptions({ input: { siloKey, spaceKey, locale: datastore.getLanguage() } }).queryKey;
}

/**
 * Apply a `spaces.update` patch to the `spaces.get` cache before the request
 * lands, so a widget gated on the space's format or features re-renders at once
 * rather than a round trip later. `features` merges into the current map, the
 * way the endpoint merges it; every other field replaces.
 *
 * Cancels an in-flight `spaces.get` first: a refetch that started before the
 * patch would otherwise resolve after it and write the pre-patch space back
 * over it, flipping the widget to the old value until the post-write
 * invalidation lands.
 *
 * Returns the entry it displaced, or `undefined` when the cache held nothing to
 * patch. Pass it back to `rollbackSpaceUpdate` if the write fails.
 */
async function applyOptimisticSpaceUpdate(datastore: Datastore, input: UpdateSpaceInput): Promise<Space | undefined> {
    const queryClient = datastore.getQueryClient();
    await queryClient.cancelQueries({ queryKey: spaceGetQueryKey(datastore, input) });
    const previous = queryClient.getQueryData<Space | null>(spaceGetQueryKey(datastore, input));
    if (!previous) return undefined;
    const { siloKey: _siloKey, spaceKey: _spaceKey, features, ...fields } = input;
    queryClient.setQueryData(spaceGetQueryKey(datastore, input), {
        ...previous,
        ...fields,
        ...(features ? { features: { ...(previous.features ?? {}), ...features } } : {}),
    });
    return previous;
}

/** Put back what `applyOptimisticSpaceUpdate` displaced, after a failure. */
function rollbackSpaceUpdate(datastore: Datastore, input: UpdateSpaceInput, previous: Space | undefined) {
    if (!previous) return;
    datastore.getQueryClient().setQueryData(spaceGetQueryKey(datastore, input), previous);
}

/**
 * Invalidate the caches affected by a `spaces.update`: the silo's
 * `spaces.list` and the specific `spaces.get`. Shared by `updateSpaceAsync`
 * and `useUpdateSpace` so the two paths can't drift.
 */
function invalidateAfterSpaceUpdate(datastore: Datastore, { spaceKey }: { spaceKey: string }) {
    const siloKey = datastore.getSiloKey();
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(
        queryClient.invalidateQueries({
            queryKey: utils.spaces.list.key({ input: { siloKey } }),
        }),
    );
    fireAndForget(
        queryClient.invalidateQueries({
            queryKey: utils.spaces.get.key({ input: { siloKey, spaceKey } }),
        }),
    );
}

/**
 * Update a space and invalidate the affected caches. Plain function —
 * callable from anywhere a `Datastore` is available, not just inside
 * React. `useUpdateSpace` is the `useMutation`-flavoured wrapper for
 * callers that want `isPending` / `onSuccess` composition.
 *
 * Optimistic: the patch reaches the `spaces.get` cache before the request, and
 * is rolled back if the write fails. An editing screen changing the space's
 * format therefore re-renders its format-gated widgets immediately, and this
 * function still rejects on failure.
 */
export async function updateSpaceAsync(datastore: Datastore, input: UpdateSpaceInput): Promise<Space> {
    const previous = await applyOptimisticSpaceUpdate(datastore, input);
    try {
        const result = await datastore.getApiClient().spaces.update({ ...input, locale: datastore.getLanguage() });
        invalidateAfterSpaceUpdate(datastore, input);
        return result;
    } catch (error) {
        rollbackSpaceUpdate(datastore, input, previous);
        throw error;
    }
}

/**
 * `useMutation`-flavoured wrapper for callers that want `isPending` /
 * `onSuccess` composition. Exposes the mutation's `mutateAsync` as
 * `updateSpaceAsync` so call sites don't have to rename it themselves.
 * Optimistic on the same terms as `updateSpaceAsync`.
 */
export function useUpdateSpace() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    const mutation = useInstanceMutation({
        procedure: utils.spaces.update,
        onMutate: async (fields) => ({
            previous: await applyOptimisticSpaceUpdate(datastore, { ...instanceParamsOf(datastore), ...fields }),
        }),
        onError: (_error, _vars, context) => rollbackSpaceUpdate(datastore, instanceParamsOf(datastore), context?.previous),
        onSuccess: () => invalidateAfterSpaceUpdate(datastore, instanceParamsOf(datastore)),
    });
    return { ...mutation, updateSpaceAsync: mutation.mutateAsync };
}

/**
 * Turn features on or off on the current space, leaving the rest of the map
 * alone. Takes a patch, so a pick-one group can flip the old key off and the
 * new one on in one write; `spaces.update` merges it server-side, so the write
 * can't drop a sibling feature that changed in the meantime.
 *
 * Optimistic, in TanStack's documented `onMutate` / `onError` / `onSettled`
 * shape: `onMutate` applies the merged map to the `spaces.get` cache, so both
 * a `useSpaceFeatures` reader and the config layer's gating move before the
 * round trip completes. `onError` restores the snapshot it returned, and
 * `mutateAsync` still rejects — how a failure is surfaced is the caller's
 * business.
 */
export function useSetSpaceFeatures() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    const params = instanceParamsOf(datastore);

    const mutation = useInstanceMutation({
        procedure: utils.spaces.update,
        input: (features: Record<string, boolean>) => ({ features }),
        onMutate: async (features) => ({ previousSpace: await applyOptimisticSpaceUpdate(datastore, { ...params, features }) }),
        onError: (_error, _vars, context) => rollbackSpaceUpdate(datastore, params, context?.previousSpace),
        onSettled: () => invalidateAfterSpaceUpdate(datastore, params),
    });
    return { ...mutation, setSpaceFeaturesAsync: mutation.mutateAsync };
}

/**
 * One named feature on the current space, for product UI that toggles a
 * feature it knows by name. Thin wrapper over `useSetSpaceFeatures`.
 */
export function useSetSpaceFeature() {
    const { setSpaceFeaturesAsync, ...mutation } = useSetSpaceFeatures();
    return {
        ...mutation,
        setSpaceFeatureAsync: ({ featureKey, value }: { featureKey: string; value: boolean }) =>
            setSpaceFeaturesAsync({ [featureKey]: value }),
    };
}

/**
 * Everything a teaser embed needs in one call, from the `spaces.teaser` oRPC
 * procedure: the space's name (resolved for the viewer's locale), its
 * top-level comment count, its participant count, and a single comment to
 * preview (the featured one if an admin set one, else the most recent
 * top-level comment). Use this instead of `useCommentStats` + `useCommentTree`
 * when all a component needs is the teaser — it skips fetching the full ranked
 * tree and reads the name straight off this payload rather than from instance
 * properties.
 *
 * Defaults to the current instance's space. Pass `targetSpaceKey` to preview a
 * different space — e.g. an article teaser rendering a preview for one of the
 * linked questions listed on the page, rather than the space it's embedded in.
 *
 * @public
 */
export function useCommentTeaser(targetSpaceKey?: string): {
    name: string;
    preview: CommentTeaserPreview | null;
    topLevelCount: number;
    participantCount: number;
    isPending: boolean;
} {
    const instanceKey = useInstanceKey();
    const spaceKey = targetSpaceKey ?? instanceKey;
    const isoLocale = useSiloLanguage();
    const utils = useApiQueryUtils();
    const { data, isPending } = useSiloQuery({ procedure: utils.spaces.teaser, input: { spaceKey } });
    const preview = data?.commentPreview ?? null;
    const name = data ? displayTextOf(data.name, isoLocale) : '';
    return { name, preview, topLevelCount: data?.topLevelCount ?? 0, participantCount: data?.participantCount ?? 0, isPending };
}

/**
 * The external stories linked into a space, from the `spaces.linkedStories`
 * oRPC procedure — the pages readers clicked through from to reach it, plus any
 * story an editor attached by hand. Defaults to an empty list while loading, so
 * a consumer that only renders a count doesn't need a loading branch.
 *
 * Public, so every consumer reads through this one hook: the space page's SEO
 * structured data (`structure/question/hooks/use-structured-data.tsx`), the
 * moderation dashboard's linked-stories widgets, and the editorial Spaces
 * list's "Linked stories" action. The dashboard and editorial screens gate at
 * the screen, the way they already do for `spaces.get` and `spaces.teaser`.
 */
export function useLinkedStories(spaceKey: string): { linkedStories: LinkedStory[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const { data, isLoading } = useSiloQuery({ procedure: utils.spaces.linkedStories, input: { spaceKey } });
    return { linkedStories: data?.items ?? [], isLoading };
}

/**
 * Feature a comment for the space's external teaser preview. Invalidates the
 * comment tree on success.
 *
 * @public
 */
export async function featureCommentAsync(datastore: Datastore, commentKey: string): Promise<Comment> {
    const result = await datastore.getApiClient().spaces.featureComment({
        ...siloParamsOf(datastore),
        spaceKey: datastore.getInstanceKey(),
        commentKey,
    });
    invalidateCommentTree(datastore);
    return result;
}

/**
 * Unfeature a comment from the space's external teaser preview. Invalidates
 * the comment tree on success.
 *
 * @public
 */
export async function unfeatureCommentAsync(datastore: Datastore, commentKey: string): Promise<Comment> {
    const result = await datastore.getApiClient().spaces.unfeatureComment({
        ...siloParamsOf(datastore),
        spaceKey: datastore.getInstanceKey(),
        commentKey,
    });
    invalidateCommentTree(datastore);
    return result;
}
